import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cn } from '../../utils';
import { GTC_META } from '../../utils/meta';
import { getGtcDataAsync } from '../store/slices/common/footerSlice';

const Container = dynamic(
  () => import('../components/Common/Container/Container')
);
const HTMLRenderer = dynamic(
  () => import('../components/Common/HTMLRenderer/HTMLRenderer')
);
const Skeleton = dynamic(() =>
  import('../components/Common/Skeleton').then((mod) => mod.Skeleton)
);
const PageContainer = dynamic(
  () => import('../components/PageContainer/PageContainer')
);

const MdKeyboardArrowRight = dynamic(() =>
  import('react-icons/md').then((mod) => mod.MdKeyboardArrowRight)
);

export async function getServerSideProps({ query }) {
  const { city } = query;
  return {
    props: {
      metaData: GTC_META(city),
    },
  };
}
const TermsAndConditions = () => {
  const [currentSection, setCurrentSection] = useState('terms-conditions');
  const router = useRouter();
  const { city } = router.query;
  const dispatch = useDispatch();
  const gtcData = useSelector(({ footer }) => {
    return footer.gtcData;
  });
  const gtcLoading = useSelector(({ footer }) => {
    return footer.gtcLoading;
  });
  const lang = useSelector(({ common }) => common?.language);

  const tabs = [
    { name: lang?.common?.ron_orp_terms_of_use, id: 'terms-conditions' },
    { name: lang?.common?.privacy_policy, id: 'privacy-policy' },
    { name: lang?.common?.digital_advertising, id: 'digital-advertising' },
  ];

  useEffect(() => {
    dispatch(getGtcDataAsync({ page: 'place-ad-on-website' }));
  }, [city]);

  const termsRef = useRef(null);
  const privacyPolicyRef = useRef(null);
  const digitalAdRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // When the target element becomes visible
          // Do something, trigger an event, or call a function
          // 'terms-conditions is in the viewport!
          setCurrentSection('terms-conditions');
        }
      },
      {
        root: null, // Use the viewport as the root
        threshold: 0.5, // Trigger when 50% of the element is visible
      }
    );

    if (termsRef.current) {
      observer.observe(termsRef.current);
    }

    // Cleanup the observer when the component unmounts
    return () => {
      if (termsRef.current) {
        observer.unobserve(termsRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // When the target element becomes visible
          // Do something, trigger an event, or call a function

          setCurrentSection('privacy-policy');
        }
      },
      {
        root: null, // Use the viewport as the root
        threshold: 0.5, // Trigger when 50% of the element is visible
      }
    );

    if (privacyPolicyRef.current) {
      observer.observe(privacyPolicyRef.current);
    }

    // Cleanup the observer when the component unmounts
    return () => {
      if (privacyPolicyRef.current) {
        observer.unobserve(privacyPolicyRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // When the target element becomes visible
          // Do something, trigger an event, or call a function

          setCurrentSection('digital-advertising');
        }
      },
      {
        root: null, // Use the viewport as the root
        threshold: 0.5, // Trigger when 50% of the element is visible
      }
    );

    if (digitalAdRef.current) {
      observer.observe(digitalAdRef.current);
    }

    // Cleanup the observer when the component unmounts
    return () => {
      if (digitalAdRef.current) {
        observer.unobserve(digitalAdRef.current);
      }
    };
  }, []);

  const SectionSkeleton = () => {
    return (
      <>
        <div className='my-4 w-full space-y-2'>
          {[...Array(8)].map((_, i) => (
            <div key={i} className='my-4 space-y-2'>
              <Skeleton className='h-4 w-full rounded' />
              <Skeleton className='h-4 w-5/6 rounded' />
              <Skeleton className='h-4 w-4/6 rounded' />
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <PageContainer>
      <Container>
        <div className='mb-[270px]'>
          <h1 className='my-8 text-center text-[36px] font-semibold uppercase md:my-[56px] md:text-[48px]'>
            {lang?.common?.terms_and_condition}
          </h1>
          <div className='flex flex-col gap-[85px] md:relative md:flex-row '>
            <div className='flex w-full flex-col  items-start gap-6 bg-white md:sticky md:top-[162px]  md:h-[300px] md:w-[187px]'>
              {tabs.map((item, index) => {
                return (
                  <Link
                    href={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/gtc?city=${city}#${item.id}`}
                    key={index}
                  >
                    <button
                      className={cn(
                        item.id === currentSection
                          ? 'font-bold md:underline md:underline-offset-[4px]'
                          : 'text-[#666]',
                        `flex  min-w-full items-center justify-between gap-2 text-[16px] md:min-w-0 md:justify-start`
                      )}
                    >
                      {item.name}
                      <MdKeyboardArrowRight className='text-[20px]' />
                    </button>
                  </Link>
                );
              })}
            </div>
            <div className='w-full'>
              <div className='mb-12 w-full'>
                <h2
                  ref={termsRef}
                  id='terms-conditions'
                  className='mb-4 text-[28px] font-semibold'
                >
                  {lang?.common?.ron_orp_terms_of_use}
                </h2>
                {gtcLoading ? (
                  <SectionSkeleton />
                ) : (
                  <HTMLRenderer html={gtcData?.terms_of_use} noStringify />
                )}
              </div>

              <div className='rich-text-editor  mb-12 w-full  max-w-[800px]'>
                <h2
                  ref={privacyPolicyRef}
                  id='privacy-policy'
                  className='mb-4 text-[28px] font-semibold'
                >
                  {lang?.common?.privacy_policy}
                </h2>
                {gtcLoading ? (
                  <SectionSkeleton />
                ) : (
                  <HTMLRenderer html={gtcData?.privacy_policy} noStringify />
                )}
              </div>
              <div className='rich-text-editor max-w-[800px]'>
                <h2
                  ref={digitalAdRef}
                  id='digital-advertising'
                  className='mb-4 text-[28px] font-semibold'
                >
                  {lang?.common?.digital_advertising}
                </h2>
                {gtcLoading ? (
                  <SectionSkeleton />
                ) : (
                  <HTMLRenderer
                    html={gtcData?.digital_advertising}
                    noStringify
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </PageContainer>
  );
};

export default TermsAndConditions;
